<template>
  <div>
    <div class="invite-participant">
      <div class="left-container">
        <FriedInput
          v-model.trim="details.email"
          v-autofocus
          data-external="new-participant-email-input"
          class="invite-input"
          :label="t('dr_invite_and_share_label')"
          :error-message="emailErrorMessage"
          autocomplete="off"
          aria-autocomplete="none"
          data-1p-ignore
          data-lpignore="true"
          @blur="checkEmail"
          @input="updateAndCheckEmail"
        />
        <TransitionFadeInOut>
          <div v-if="hasStartedEditing" class="fields-container">
            <FriedInput
              v-model.trim="details.firstName"
              :label="t('first_name_optional')"
              data-external="new-participant-first-name-input"
              autocomplete="off"
              @input="$emit('update', details)"
            />
            <FriedInput
              v-model.trim="details.lastName"
              :label="t('last_name_optional')"
              data-external="new-participant-last-name-input"
              autocomplete="off"
              @input="$emit('update', details)"
            />
            <FriedInput
              v-model.trim="details.companyName"
              :label="t('company_optional')"
              data-external="new-participant-company-name-input"
              autocomplete="off"
              @input="$emit('update', details)"
            />
            <FriedInput
              v-model.trim="details.title"
              :label="t('title_optional')"
              data-external="new-participant-title-input"
              autocomplete="off"
              @input="$emit('update', details)"
            />
          </div>
        </TransitionFadeInOut>
      </div>
      <FriedButton
        v-if="showRemoveButton"
        data-external="remove-participant-button"
        :button-type="ButtonType.Tertiary"
        @click="removeParticipant"
      >
        <template #leftIcon>
          <FriedIcon icon="close-circle" />
        </template>
      </FriedButton>
    </div>
    <hr v-if="participantsToInvite.length > 1 ? !isLast : hasStartedEditing" class="divider" />
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Participant } from '@getaccept/dsr-shared-new';
import { ButtonType } from '@getaccept/fried-tofu';
import debounce from 'debounce';
import type { ParticipantInvite } from '../types/participant-invite';
import { ShareHelper } from '../helpers/share.helper';

export default defineComponent({
  props: {
    participant: {
      type: Object as PropType<ParticipantInvite>,
      default: () => ({}),
    },
    currentParticipants: {
      type: Array as PropType<Participant[]>,
      default: () => [],
    },
    participantsToInvite: {
      type: Array as PropType<ParticipantInvite[]>,
      default: () => [],
    },
    isLast: {
      type: Boolean,
    },
    hasStartedEditing: {
      type: Boolean,
    },
  },
  emits: ['update', 'remove'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const details = ref({
      id: props.participant.id,
      email: props.participant.email,
      firstName: props.participant.firstName,
      lastName: props.participant.lastName,
      companyName: props.participant.companyName,
      title: props.participant.title,
    });
    const emailErrorMessage = ref('');

    const showRemoveButton = computed(() =>
      props.participantsToInvite.length === 1 ? props.hasStartedEditing : true
    );

    const checkEmail = () => {
      emailErrorMessage.value = ShareHelper.validateEmail(
        details.value.email,
        props.currentParticipants,
        props.participantsToInvite
      );
    };

    const removeParticipant = () => {
      if (props.participantsToInvite.length === 1) {
        details.value = {
          id: props.participant.id,
          email: '',
          firstName: '',
          lastName: '',
          companyName: '',
          title: '',
        };
        emailErrorMessage.value = '';
      }
      emit('remove');
    };

    const debounceCheckEmail = debounce(checkEmail, 300);

    const updateAndCheckEmail = () => {
      emit('update', details.value);
      if (emailErrorMessage.value) {
        debounceCheckEmail();
      }
    };

    return {
      details,
      blur,
      emailErrorMessage,
      t,
      showRemoveButton,
      ButtonType,
      checkEmail,
      removeParticipant,
      updateAndCheckEmail,
    };
  },
});
</script>

<style lang="scss" scoped>
.invite-participant {
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-50);

  .left-container {
    flex: 1;
  }

  .invite-input {
    width: 100%;
    margin-bottom: var(--spacing-50);
  }

  .fields-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: var(--spacing-75);
    row-gap: var(--spacing-50);
  }

  :deep(.button) {
    min-width: unset;

    .button-icon.left {
      margin-right: 0;
    }
  }
}

.divider {
  margin-top: var(--spacing-150);
  border: 1px solid var(--gray-94);
  margin-bottom: var(--spacing-100);
}
</style>
